import type { ComponentDocs } from '../component-docs.js';

/**
 * What `<caos-tooltip>` says about itself.
 *
 * The piece WRAPS what it describes rather than pointing at it: the trigger is the default slot, so
 * an example with nothing in it is a tooltip with nothing to hover, which is an example of nothing.
 * Both examples here therefore hold a real control.
 *
 * ON THE ROOM. The tip sits ABOVE what it describes at the default placement, so the space it needs
 * is over the piece rather than under it — 48px, measured against the fixture below: a 35px tip plus
 * the 6px gap, rounded up. Change the tip text to something that wraps to two lines and the number
 * is too small; that is a cosmetic failure on this page and nothing more, because a stage that
 * declares room clips nothing. The second example moves the tip below the control, and the room
 * moves with it — which is the whole point of the second example.
 */
const docs: ComponentDocs = {
  aliases: ['tip', 'hint', 'hover text', 'title text', 'infotip'],

  answers: [
    'say what an icon-only control does',
    'explain a label without spending screen space on it',
    'put a definition on a column heading',
    'add a hint somebody can reach with the keyboard, not just the pointer',
  ],

  // A tooltip belongs anywhere a control it describes belongs, including the frame's own controls —
  // the piece it wraps is what decides where it stands, not the tooltip.
  placements: ['recordPage', 'homePage', 'appPage', 'screen', 'utilityBar', 'seam'],

  inputs: [
    {
      name: 'text',
      attribute: 'text',
      type: 'text',
      description:
        'The tip. A short phrase describing the thing it wraps. It is ignored when something is ' +
        'slotted into the tip slot, so the two never fight over the bubble.',
    },
    {
      name: 'placement',
      attribute: 'placement',
      type: 'text',
      options: ['top', 'bottom', 'left', 'right'],
      description:
        'Which side of the trigger the bubble prefers: top, bottom, left or right. Where that side has ' +
        'no room and the opposite side does, it opens there instead, and it slides along the side it ' +
        'opens on so the whole bubble stays on screen.',
      default: 'top',
    },
  ],

  // Declared as NONE rather than left unsaid: it raises nothing. It shows and hides itself from
  // hover and focus of its own trigger, and there is no state a host would need to hear about.
  events: [],

  state: [],

  slots: [
    {
      name: '',
      takes:
        'The trigger — the thing being described. It is wrapped in a focusable span, so it is ' +
        'reachable by keyboard even when what it holds is plain text.',
    },
    {
      name: 'tip',
      takes: 'Richer tip content than a line of text, for the cases the text attribute cannot carry. Never an action.',
    },
  ],

  tokens: [
    { token: '--caos-color-surface', controls: 'The fill of the bubble.' },
    { token: '--caos-color-border', controls: 'The edge of the bubble.' },
    { token: '--caos-color-text', controls: 'The tip text.' },
    { token: '--caos-shadow-float', controls: 'The one elevation reserved for pieces that float.' },
    { token: '--caos-radius-sm', controls: 'The corner radius of the bubble, and of the focus ring on the trigger.' },
    { token: '--caos-focus-ring', controls: 'The ring drawn on the trigger when it is reached by keyboard.' },
    { token: '--caos-space-2', controls: 'The vertical padding inside the bubble.' },
    { token: '--caos-space-3', controls: 'The horizontal padding inside the bubble.' },
    { token: '--caos-font', controls: 'The typeface the tip is set in.' },
  ],

  parts: [
    { part: 'trigger', is: 'the thing being described, which the tip hangs off' },
    { part: 'tip', is: 'the tip itself' },
  ],

  methods: [],

  placing:
    'It is placed around something rather than beside it: whatever it describes goes inside it, and ' +
    'the tooltip itself is an inline box that takes no more room than that. What it needs from the ' +
    'page is room on the side the tip opens toward — the bubble reaches past the tooltip on that ' +
    'side, so a container that clips its own overflow will cut the tip off.',

  whenToUse:
    'A short description of one control, wanted only by somebody who is already looking at it. If ' +
    'the words matter enough that everybody should read them, they should not be hidden behind a ' +
    'hover.',

  insteadUse: [
    {
      name: 'InlineHint',
      when: 'The explanation is worth permanent screen space — it says what a field drives, and everybody needs it.',
    },
    {
      name: 'OverflowMenu',
      when: 'What opens carries actions. A tooltip never does: it is not reachable by pointer and holds nothing to press.',
    },
    {
      name: 'Toast',
      when: 'The message is about something that just happened rather than about the control under the pointer.',
    },
  ],

  accessibility:
    'The trigger wrapper is focusable and points aria-describedby at the bubble, which carries ' +
    'role="tooltip", so the tip is announced as a description of the thing it wraps. It reveals on ' +
    'focus as well as on hover, so it is reachable without a pointer, and Escape blurs the trigger to ' +
    'dismiss it. Hidden, it is out of the accessibility tree entirely rather than merely invisible. ' +
    'What is left to the author: this is a DESCRIPTION, not a name — an icon-only button still needs ' +
    'its own accessible label, and a tooltip is not a substitute for one.',

  examples: [
    {
      key: 'on-a-control',
      label: 'On a control',
      summary: 'The ordinary case: a control wrapped in the tip that describes it, opening above at the default placement.',
      placement: {
        component: 'caos-tooltip',
        inputs: { text: 'Adds an empty row to the table below.', placement: 'top' },
        children: [{ component: 'caos-button', inputs: { variant: 'secondary' }, children: ['Add line'] }],
      },
      // The tip sits ABOVE what it describes, so the room this one needs is over the piece, not
      // under. Measured: 35px tip + 6px gap.
      stage: { opensAbove: 48 },
    },
    {
      key: 'below',
      label: 'Opening downward',
      summary:
        'The same tooltip with placement set to bottom — the side the author prefers, and the ' +
        'room the surrounding page has to leave clear moves with it.',
      placement: {
        component: 'caos-tooltip',
        inputs: { text: 'Adds an empty row to the table below.', placement: 'bottom' },
        children: [{ component: 'caos-button', inputs: { variant: 'secondary' }, children: ['Add line'] }],
      },
      // The same 35px tip and 6px gap as above, on the other side of the trigger.
      stage: { opensBelow: 48 },
    },
  ],
};

export default docs;
